import { Save, Cancel } from '@mui/icons-material';
import {
  Grid,
  TextField,
  FormHelperText,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Button,
  CircularProgress
} from '@mui/material';

import { Formik, Field } from 'formik';
import { pick } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { useJobPriceQuery, useJobPricesMutation } from 'api/hooks/jobmarket/jobprices';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

interface Props {
  closeHandler: Function;
  priceId?: number;
  newPrice?: boolean;
}

export default function PriceDetail(props: Props) {
  const { t } = useTranslation();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data, isError, isLoading } = useJobPriceQuery(props.priceId);

  const { create, update } = useJobPricesMutation();

  const handleCancelClick = dirty => {
    if (dirty) {
      setCancelModalOpen(true);
    } else {
      props.closeHandler();
    }
  };

  const handleSaveClick = async values => {
    try {
      if (props.newPrice) {
        await create.mutateAsync(values);
      } else {
        if (!props.priceId) {
          return;
        }
        await update.mutateAsync({
          id: props.priceId,
          data: pick(values, 'name', 'duration', 'factor', 'price')
        });
      }
      props.closeHandler();
      dispatch(
        AlertActions.success(
          t(translations.pages.manageJobmarketSettings.alerts.savePriceSuccessful())
        )
      );
    } catch (err) {
      dispatch(
        AlertActions.error(t(translations.pages.manageJobmarketSettings.alerts.savePriceFailed()))
      );
    }
  };

  if (isError) {
    dispatch(
      AlertActions.error(`${t(translations.pages.manageJobmarketSettings.alerts.errorLoading())}`)
    );
    navigate('/', { replace: true });
    return;
  }

  if (isLoading) {
    return <CircularProgress />;
  }

  const priceData = data || {
    name: '',
    price: 0,
    duration: 1,
    factor: 'month'
  };

  return (
    <Formik
      initialValues={priceData}
      validationSchema={yup.object().shape({
        name: yup.string().required(),
        duration: yup.number().required().min(0),
        factor: yup.string().required().oneOf(['day', 'week', 'month', 'year']),
        price: yup.number().min(0).required()
      })}
      onSubmit={handleSaveClick}
    >
      {({ dirty, handleSubmit, errors }) => {
        return (
          <form onSubmit={handleSubmit}>
            <ConfirmationDialog
              open={cancelModalOpen}
              title={t(translations.common.confirmCancel())}
              onClose={() => {
                setCancelModalOpen(false);
              }}
              onConfirm={() => {
                setCancelModalOpen(false);
                props.closeHandler();
              }}
            >
              <div>{t(translations.pages.manageJobmarketSettings.cancelConfirm.message())}</div>
            </ConfirmationDialog>
            <Grid container gap={1}>
              <Grid item xs={12} md>
                <Field
                  className="type-detail-modal__body__field"
                  as={TextField}
                  name="name"
                  error={errors.name}
                  helperText={errors.name}
                  required
                  label={t(translations.common.name())}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md>
                <Field
                  className="type-detail-modal__body__field"
                  as={TextField}
                  name="price"
                  type="number"
                  error={errors.price}
                  helperText={errors.price}
                  required
                  label={t(translations.pages.manageJobmarketSettings.price())}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid container gap={1}>
                <Grid item xs={12} md>
                  <Field
                    className="type-detail-modal__body__field"
                    as={TextField}
                    name="duration"
                    type="number"
                    error={errors.duration}
                    helperText={errors.duration}
                    required
                    label={t(translations.pages.manageJobmarketSettings.duration())}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md>
                  <Field name="factor">
                    {({ form, field }) => (
                      <FormControl required error={form.errors.factor} fullWidth>
                        <InputLabel id="test">
                          {t(translations.pages.manageJobmarketSettings.factor())}
                        </InputLabel>
                        <Select
                          value={form.values.factor}
                          onChange={field.onChange(field.name)}
                          fullWidth
                          size="small"
                          label={t(translations.pages.manageJobmarketSettings.factor())}
                        >
                          <MenuItem value="day">
                            {t(translations.pages.manageJobmarketSettings.factors.day())}
                          </MenuItem>
                          <MenuItem value="week">
                            {t(translations.pages.manageJobmarketSettings.factors.week())}
                          </MenuItem>
                          <MenuItem value="month">
                            {t(translations.pages.manageJobmarketSettings.factors.month())}
                          </MenuItem>
                          <MenuItem value="year">
                            {t(translations.pages.manageJobmarketSettings.factors.year())}
                          </MenuItem>
                        </Select>
                        {form.errors.factor && (
                          <FormHelperText>{form.errors.factor}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  </Field>
                </Grid>
                <Grid container alignContent={'flex-end'} justifyContent={'flex-end'} gap={2}>
                  <Button
                    color="error"
                    startIcon={<Cancel />}
                    size="small"
                    onClick={() => {
                      handleCancelClick(dirty);
                    }}
                  >
                    {t(translations.common.cancel())}
                  </Button>
                  <Button type="submit" startIcon={<Save />} variant="outlined" size="small">
                    {t(translations.common.save())}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </form>
        );
      }}
    </Formik>
  );
}
